import React, { useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Send, Lock, XCircle, Download, AlertTriangle,
} from 'lucide-react';

import DataTable from '../../../components/ui/DataTable';
import TextInput from '../../../components/ui/TextInput.jsx';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import CloseCycleDialog from './CloseCycleDialog';
import {
  STATUS_COLORS, StatusBadge, computeMissingRecommendations, computeDeferredRecommendations,
} from '../config';

const Container = styled.div``;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.primary.main};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
`;

const CycleName = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TypeLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
`;

const MissingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  background: #FFF7ED;
  color: #EA580C;
  font-size: 11px;
  font-weight: 700;
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 16px;
`;

const CardTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 10px;
`;

const MatrixRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  gap: 6px 10px;
  font-size: 12px;
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MatrixHeaderRow = styled(MatrixRow)`
  font-weight: 700;
  color: #64748B;
  border-bottom: 1px solid #E8ECF0;
  padding-bottom: 6px;
`;

const EmptyHint = styled.div`
  padding: 40px;
  text-align: center;
  color: #A0A8B4;
  font-size: 13px;
`;

const InlineCellWrap = styled.div`
  min-width: 90px;
`;

const ReadOnlyCell = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Faz G (spec 2026-08-08 §3/§4, Task 4) — İK tur detayı: matris özeti (salt
// okunur) + öneri tablosu (çalışan/departman/seviye/öneri%/not/final%/durum).
// İK final%/not düzenlemesi GERÇEKTEN inline (tablo hücresinde, ayrı bir
// modal YOK) — CompRecommendationFinalizeRequest'in iki alanının BAĞIMSIZ/
// PATCH-benzeri olması (task-2-report.md §0 C2) bunu doğal kılıyor: her alan
// kendi debounce'lu TextInput'undan yalnız KENDİ payload'ını gönderir, diğer
// alana ASLA dokunmaz. Yalnız cycle.status === 'review' iken düzenlenebilir
// (BE FinalizeRecommendation yalnız review'de kabul eder — invalidState
// aksi halde, task-2-report.md §7).
export default function CycleDetailView({
  cycle, fetching, onBack, onRunLifecycle, onFinalize, onCloseCycle, onExport,
}) {
  const { t } = useTranslation();

  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [reviewConfirm, setReviewConfirm] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const recommendations = useMemo(() => cycle?.recommendations || [], [cycle]);
  const missingNames = useMemo(() => computeMissingRecommendations(recommendations), [recommendations]);
  // Kapanmış turda hâlâ kesinleşmemiş satırlar (İK'nın kendi satırı — Close onu erteler).
  const deferredNames = useMemo(
    () => (cycle?.status === 'closed' ? computeDeferredRecommendations(recommendations) : []),
    [cycle, recommendations],
  );
  const canEdit = cycle?.status === 'review';

  const handleFinalPctChange = useCallback((row, rawValue) => {
    const trimmed = (rawValue ?? '').toString().trim();
    if (trimmed === '') return;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return;
    if (n === row.finalPct) return;
    onFinalize(row.id, { finalPct: n });
  }, [onFinalize]);

  const handleHrNoteChange = useCallback((row, rawValue) => {
    const value = rawValue ?? '';
    if (value === (row.hrNote || '')) return;
    onFinalize(row.id, { hrNote: value });
  }, [onFinalize]);

  const handleReviewConfirm = useCallback(async () => {
    setActionLoading(true);
    await onRunLifecycle(cycle.id, 'review');
    setActionLoading(false);
    setReviewConfirm(false);
  }, [onRunLifecycle, cycle]);

  const handleCancelConfirm = useCallback(async () => {
    setActionLoading(true);
    await onRunLifecycle(cycle.id, 'cancel');
    setActionLoading(false);
    setCancelConfirm(false);
  }, [onRunLifecycle, cycle]);

  const handleCloseSubmit = useCallback(async () => onCloseCycle(), [onCloseCycle]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'employeeName', label: t('compCycles.fields.employee'), options: { filter: false, sort: false } },
    {
      name: 'departmentName',
      label: t('compCycles.fields.department'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'levelLabel',
      label: t('compCycles.fields.level'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'proposedPct',
      label: t('compCycles.fields.proposedPct'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v == null ? '-' : `${v}%`) },
    },
    {
      name: 'proposedNote',
      label: t('compCycles.fields.proposedNote'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'finalPct',
      label: t('compCycles.fields.finalPct'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = recommendations[tableMeta.rowIndex];
          if (!row) return null;
          if (!canEdit) return <ReadOnlyCell>{v == null ? '-' : `${v}%`}</ReadOnlyCell>;
          return (
            <InlineCellWrap>
              <TextInput
                type="number"
                min="0"
                max="100"
                placeholder="%"
                value={v ?? ''}
                debounceMs={600}
                onChange={(e) => handleFinalPctChange(row, e.target.value)}
              />
            </InlineCellWrap>
          );
        },
      },
    },
    {
      name: 'hrNote',
      label: t('compCycles.fields.hrNote'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = recommendations[tableMeta.rowIndex];
          if (!row) return null;
          if (!canEdit) return <ReadOnlyCell>{v || '-'}</ReadOnlyCell>;
          return (
            <InlineCellWrap>
              <TextInput
                value={v ?? ''}
                debounceMs={600}
                onChange={(e) => handleHrNoteChange(row, e.target.value)}
              />
            </InlineCellWrap>
          );
        },
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: true,
        sort: false,
        customBodyRender: (v) => t(`compCycles.recommendationStatus.${v}`, { defaultValue: v }),
      },
    },
  ], [t, recommendations, canEdit, handleFinalPctChange, handleHrNoteChange]);

  if (fetching) {
    return <Container><EmptyHint>{t('common.loading')}</EmptyHint></Container>;
  }

  if (!cycle) {
    return (
      <Container>
        <BackButton type="button" onClick={onBack}><ArrowLeft size={14} />{t('compCycles.backToList')}</BackButton>
        <EmptyHint>{t('compCycles.detail.notFound')}</EmptyHint>
      </Container>
    );
  }

  const statusCfg = STATUS_COLORS[cycle.status] || STATUS_COLORS.draft;
  const sortedGuidelines = (cycle.guidelines || []).slice().sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Container>
      {reviewConfirm && (
        <ConfirmDialog
          isOpen={reviewConfirm}
          title={t('compCycles.reviewConfirm.title')}
          message={t('compCycles.reviewConfirm.message')}
          onConfirm={handleReviewConfirm}
          onCancel={() => setReviewConfirm(false)}
          loading={actionLoading}
          type="info"
        />
      )}

      {cancelConfirm && (
        <ConfirmDialog
          isOpen={cancelConfirm}
          title={t('compCycles.cancelConfirm.title')}
          message={t('compCycles.cancelConfirm.message')}
          onConfirm={handleCancelConfirm}
          onCancel={() => setCancelConfirm(false)}
          loading={actionLoading}
          type="danger"
        />
      )}

      {closeDialogOpen && (
        <CloseCycleDialog
          recommendations={recommendations}
          onClose={() => setCloseDialogOpen(false)}
          onSubmit={handleCloseSubmit}
        />
      )}

      <BackButton type="button" onClick={onBack}><ArrowLeft size={14} />{t('compCycles.backToList')}</BackButton>

      <HeaderRow>
        <TitleGroup>
          <CycleName>{cycle.name}</CycleName>
          <StatusBadge color={statusCfg.color} bg={statusCfg.bg}>
            {t(`compCycles.status.${cycle.status}`, { defaultValue: cycle.status })}
          </StatusBadge>
          <TypeLabel>{t(`compCycles.type.${cycle.cycleType}`, { defaultValue: cycle.cycleType })}</TypeLabel>
          {cycle.status === 'review' && missingNames.length > 0 && (
            <MissingBadge data-role="missing-badge">
              <AlertTriangle size={12} />
              {t('compCycles.detail.missingBadge', { count: missingNames.length, defaultValue: `${missingNames.length}` })}
            </MissingBadge>
          )}
          {deferredNames.length > 0 && (
            <MissingBadge
              data-role="deferred-badge"
              // Adlar HEM çeviri metnine (kullanıcıya) HEM de ayrı bir veri niteliğine
              // yazılır: i18n interpolasyonu üzerinden "adlar gerçekten taşınıyor mu"
              // sorusu dışarıdan gözlenemez (kimlik t() mock'u anahtarı döner) ve rozet
              // "1 satır açık" deyip KİMİN satırı olduğunu yutabilirdi.
              data-deferred-names={deferredNames.join(', ')}
              title={t('compCycles.detail.deferredHint', { names: deferredNames.join(', ') })}
            >
              <AlertTriangle size={12} />
              {t('compCycles.detail.deferredBadge', { count: deferredNames.length, defaultValue: `${deferredNames.length}` })}
            </MissingBadge>
          )}
        </TitleGroup>
        <ActionsRow>
          <Button variant="outline" onClick={() => onExport(cycle)}>
            <Download size={14} />
            {t('compCycles.actions.export')}
          </Button>
          {cycle.status === 'active' && (
            <Button onClick={() => setReviewConfirm(true)}>
              <Send size={14} />
              {t('compCycles.actions.review')}
            </Button>
          )}
          {cycle.status === 'review' && (
            <Button data-role="open-close-dialog-btn" onClick={() => setCloseDialogOpen(true)}>
              <Lock size={14} />
              {t('compCycles.actions.close')}
            </Button>
          )}
          {['draft', 'active', 'review'].includes(cycle.status) && (
            <Button variant="outline" onClick={() => setCancelConfirm(true)}>
              <XCircle size={14} />
              {t('compCycles.actions.cancel')}
            </Button>
          )}
        </ActionsRow>
      </HeaderRow>

      <Card>
        <CardTitle>{t('compCycles.detail.matrixTitle')}</CardTitle>
        {sortedGuidelines.length === 0 ? (
          <EmptyHint>{t('compCycles.detail.matrixEmpty')}</EmptyHint>
        ) : (
          <>
            <MatrixHeaderRow>
              <span>{t('compCycles.fields.level')}</span>
              <span>{t('compCycles.fields.minPct')}</span>
              <span>{t('compCycles.fields.targetPct')}</span>
              <span>{t('compCycles.fields.maxPct')}</span>
            </MatrixHeaderRow>
            {sortedGuidelines.map((g) => (
              <MatrixRow key={`${g.levelId}-${g.gradeId || 'all'}`}>
                {/* Kademe satırı seviyenin genel satırının altında (spec 2026-10-27 §7.7). */}
                <span>{g.gradeId ? `${g.levelLabel} · ${g.gradeName || ''}` : g.levelLabel}</span>
                <span>{g.minPct}%</span>
                <span>{g.targetPct}%</span>
                <span>{g.maxPct}%</span>
              </MatrixRow>
            ))}
          </>
        )}
      </Card>

      <DataTable
        title={t('compCycles.detail.recommendationsTitle')}
        fetching={fetching}
        columns={columns}
        data={recommendations}
        serverSide={false}
        canCreateNewRecord={false}
      />
    </Container>
  );
}
